import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";

import styles from "./AppShell.module.css";
import { BackToTop } from "./BackToTop";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";

import { FEATURES } from "@/app/features";
import { SettingsIcon } from "@/components/icons";
import { ROUTES } from "@/config/routes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { NavItem as NavItemType } from "@/types/navigation";

/**
 * The persistent application frame (prototype layout): a full-width header
 * (brand, search, account menu) over the dark-chrome sidebar (manual tree +
 * tools) and the routed page. Below 1024px the sidebar becomes an off-canvas
 * drawer behind a header hamburger. Tool entries come from the feature
 * registry, filtered by the caller's permissions; the manual tree inside
 * Sidebar comes from /api/cms/nav. Every page renders its own heading, so
 * there is no second, duplicate title bar here.
 */
export function AppShell() {
  const location = useLocation();
  const { permissions } = useCurrentUser();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const contentRef = useRef<HTMLElement>(null);

  // Navigating anywhere closes the drawer; so does Escape.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  // Tools: feature nav items that declare a permission (Approvals, editing…),
  // plus the Settings slot for rbac.manage holders.
  const tools: NavItemType[] = FEATURES.flatMap((f) => f.navItems).filter(
    (item) => item.requires && permissions.includes(item.requires)
  );
  if (permissions.includes("rbac.manage")) {
    tools.push({
      id: "settings",
      // Named for what the page actually is (Access management) — the
      // generic "Settings" label pointed at a people/roles page.
      label: "People & access",
      path: ROUTES.settingsAccess,
      icon: SettingsIcon,
    });
  }

  return (
    <div className={styles.app}>
      <Header drawerOpen={drawerOpen} onToggleDrawer={() => setDrawerOpen((v) => !v)} />
      {drawerOpen && (
        <div className={styles.scrim} aria-hidden="true" onClick={() => setDrawerOpen(false)} />
      )}
      <div className={drawerOpen ? `${styles.sidebarWrap} ${styles.open}` : styles.sidebarWrap}>
        <Sidebar tools={tools} activePath={location.pathname} />
      </div>
      <main className={styles.main} id="main" tabIndex={-1}>
        <section className={styles.content} aria-label="Page content" ref={contentRef}>
          <Outlet />
        </section>
        <BackToTop target={contentRef} routeKey={location.pathname} />
      </main>
    </div>
  );
}
